import { literalListSerialiser } from "hooks/serialisers/literalList";
import { useQueryParamState } from "hooks/useQueryParamState";
import { useCallback, useMemo } from "react";

/**
 * Resolves the stored selection from a localStorage value, keeping only
 * currently-permitted entries and returning `defaultValue` when the value is
 * absent, unparseable, or entirely stale.
 */
export const parsePermittedList = <T extends string>(
  storedValue: string | null,
  permitted: readonly T[],
  defaultValue: readonly T[]
): T[] => {
  if (storedValue === null) {
    return [...defaultValue];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(storedValue);
  } catch {
    return [...defaultValue];
  }
  if (!Array.isArray(parsed)) {
    return [...defaultValue];
  }
  if (parsed.length === 0) {
    return [];
  }
  const permittedSet = new Set<string>(permitted);
  const filtered = parsed.filter(
    (entry): entry is T => typeof entry === "string" && permittedSet.has(entry)
  );
  return filtered.length > 0 ? filtered : [...defaultValue];
};

/**
 * Selection of a subset of string literals, kept in a URL query param and
 * mirrored to localStorage. The query param takes precedence when present,
 * otherwise the localStorage value, then `defaultValue`.
 */
export const useLiteralListQueryParamState = <T extends string>({
  paramName,
  permitted,
  localStorageKey,
  defaultValue,
}: {
  paramName: string;
  permitted: readonly T[];
  localStorageKey: string;
  defaultValue: readonly T[];
}): [T[], (next: T[]) => void] => {
  const serialiser = useMemo(
    () => literalListSerialiser(permitted),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- permitted is a fresh array each render; key on its contents to keep the serialiser identity stable
    [JSON.stringify(permitted)]
  );

  const seededDefault = useMemo(
    () =>
      parsePermittedList(
        localStorage.getItem(localStorageKey),
        permitted,
        defaultValue
      ),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- seed once at mount; localStorage only changes via this hook's setter
    []
  );

  const [values, setValue] = useQueryParamState<T[], never>(
    paramName,
    serialiser,
    seededDefault
  );

  const setValues = useCallback(
    (next: T[]) => {
      setValue(next);
      localStorage.setItem(localStorageKey, JSON.stringify(next));
    },
    [setValue, localStorageKey]
  );

  return [values, setValues];
};
